import React from 'react'
import { Card, Form, Button, Alert, Typography, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import type { FormInstance } from 'antd/es/form'

const { Paragraph, Text, Title } = Typography

// 使用索引签名类型
export interface AuthFormProps<T extends Record<string, unknown>> {
    title: string
    formName: string
    onFinish: (values: T) => Promise<void>
    loading: boolean
    error: string | null
    children: React.ReactNode
    successMessage?: string | null
    submitText?: string
    footerContent?: React.ReactNode
    form?: FormInstance<T>
    subtitle?: string
    eyebrow?: string
    containerClassName?: string
    cardClassName?: string
}

const AuthForm = <T extends Record<string, unknown>>({
    title,
    formName,
    onFinish,
    loading,
    error,
    children,
    successMessage,
    submitText,
    footerContent,
    form,
    subtitle,
    eyebrow,
    containerClassName,
    cardClassName,
}: AuthFormProps<T>): React.ReactElement => {
    const { t } = useTranslation('auth')
    const containerClasses = ['auth-form-container', containerClassName]
        .filter(Boolean)
        .join(' ')
    const cardClasses = ['auth-form-card', cardClassName]
        .filter(Boolean)
        .join(' ')
    
    return (
        <div className={containerClasses}>
            <Card variant="borderless" className={cardClasses}>
                <Space
                    direction="vertical"
                    size="large"
                    style={{ width: '100%' }}
                >
                    <div className="auth-form-header">
                        {eyebrow && (
                            <Text className="auth-form-eyebrow">
                                {eyebrow}
                            </Text>
                        )}
                        <Title level={2}>{title}</Title>
                        {subtitle && (
                            <Paragraph
                                type="secondary"
                                className="auth-form-subtitle"
                            >
                                {subtitle}
                            </Paragraph>
                        )}
                    </div>

                    {successMessage && (
                        <Alert
                            message={successMessage}
                            type="success"
                            showIcon
                        />
                    )}

                    {error && <Alert message={error} type="error" showIcon />}

                    <Form
                        form={form}
                        name={formName}
                        onFinish={(values) => onFinish(values as T)}
                        layout="vertical"
                        autoComplete="off"
                        requiredMark={false}
                    >
                        {children}

                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                block
                                size="large"
                            >
                                {submitText || t('common.submit')}
                            </Button>
                        </Form.Item>
                    </Form>

                    {footerContent}
                </Space>
            </Card>
        </div>
    )
}

export default AuthForm
